import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, User, Clock, Share, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiGet } from '../utils/api';

const BlogPostPage = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumeUrl, setResumeUrl] = useState('');
  const [interviewUrl, setInterviewUrl] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await apiGet(`/api/blog/${id}`);
        if (active) setPost(data);
      } catch (e) {
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resources = await apiGet('/api/resources');
        const resume = resources.find(r => (r.title || '').toLowerCase().includes('resume'));
        const interview = resources.find(r => (r.title || '').toLowerCase().includes('interview'));
        if (active) {
          setResumeUrl(resume?.fileUrl || '');
          setInterviewUrl(interview?.fileUrl || '');
        }
      } catch (e) {}
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="pt-16 pb-12 text-center text-slate-600">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Header />
        <div className="pt-16 pb-12 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      <article className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>

          <div className="mb-4">
            <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-semibold">{post.category}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">{post.title}</h1>

          <div className="flex flex-wrap items-center text-slate-600 mb-8">
            <div className="flex items-center mr-6 mb-2">
              <User className="h-4 w-4 mr-2" />
              {post.author}
            </div>
            <div className="flex items-center mr-6 mb-2">
              <Calendar className="h-4 w-4 mr-2" />
              {post.date}
            </div>
            <div className="flex items-center mr-6 mb-2">
              <Clock className="h-4 w-4 mr-2" />
              {post.readTime}
            </div>
            <button className="flex items-center text-indigo-600 hover:text-indigo-700 mb-2">
              <Share className="h-4 w-4 mr-2" />
              Share
            </button>
          </div>

          <div className="mb-12">
            <img src={post.image} alt={post.title} className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl shadow-lg" />
          </div>

          <div className="prose prose-lg max-w-none">
            {post.content.split('\n').map((paragraph, index) => {
              if (paragraph.trim() === '') return null;
              if (paragraph.startsWith('# ')) {
                return (
                  <h1 key={index} className="text-3xl font-bold text-slate-900 mb-6 mt-12">{paragraph.replace('# ', '')}</h1>
                );
              }
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-2xl font-bold text-slate-900 mb-4 mt-10">{paragraph.replace('## ', '')}</h2>
                );
              }
              if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="text-xl font-bold text-slate-900 mb-3 mt-8">{paragraph.replace('### ', '')}</h3>
                );
              }
              if (paragraph.startsWith('#### ')) {
                return (
                  <h4 key={index} className="text-lg font-bold text-slate-900 mb-2 mt-6">{paragraph.replace('#### ', '')}</h4>
                );
              }
              if (paragraph.startsWith('*') && paragraph.endsWith('*')) {
                return (
                  <p key={index} className="italic text-slate-600 mb-4 text-center bg-indigo-50 p-4 rounded-lg">{paragraph.replace(/^\*/, '').replace(/\*$/, '')}</p>
                );
              }
              if (paragraph.startsWith('**') && paragraph.includes('**')) {
                return (
                  <p key={index} className="font-semibold text-slate-800 mb-4">{paragraph.replace(/\*\*/g, '')}</p>
                );
              }
              if (paragraph.startsWith('- ') || paragraph.startsWith('• ')) {
                return (
                  <li key={index} className="text-slate-700 mb-2 ml-6">{paragraph.replace(/^[•-] /, '')}</li>
                );
              }
              if (paragraph.trim().length > 0) {
                return (
                  <p key={index} className="text-slate-700 mb-4 leading-relaxed">{paragraph}</p>
                );
              }
              return null;
            })}
          </div>

          {/* Resource CTA block */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-indigo-50 border border-indigo-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Download ATS‑Friendly Resume Template</h3>
              <p className="text-slate-700 mb-4">Build a resume that passes ATS filters and impresses hiring managers.</p>
              <a href={resumeUrl || '/resources'} target={resumeUrl ? '_blank' : undefined} rel={resumeUrl ? 'noopener noreferrer' : undefined} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-5 py-3 rounded-md">
                <Download className="w-4 h-4" /> Download Template
              </a>
            </div>
            <div className="p-6 rounded-2xl bg-indigo-50 border border-indigo-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Top 100 IT Interview Questions & Answers</h3>
              <p className="text-slate-700 mb-4">Prepare for interviews with technical and behavioral questions and model answers.</p>
              <a href={interviewUrl || '/resources'} target={interviewUrl ? '_blank' : undefined} rel={interviewUrl ? 'noopener noreferrer' : undefined} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-5 py-3 rounded-md">
                <Download className="w-4 h-4" /> Download Checklist
              </a>
            </div>
          </div>

          <div className="mt-12 p-8 bg-indigo-700 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Career?</h3>
            <p className="text-indigo-100 mb-6">Get personalized guidance and support for your IT career journey.</p>
            <Button className="bg-white text-indigo-700 hover:bg-gray-100 font-bold px-8 py-3 rounded-full">Schedule Free Consultation</Button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
